// Opens the JiveBird app from the mobile website, or falls back to the web URL.
//
// App paths (handled by the app's deep-link router):
//   home            -> app landing screen
//   create          -> JiveBird builder
//   card/<occasion> -> card picker for that occasion, e.g. card/thank-you

export const APP_STORE_URL = 'https://apps.apple.com/app/id946644356';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.greetings.jivebird';

const APP_SCHEME = 'jivebird';
const ANDROID_PACKAGE = 'com.greetings.jivebird';

/**
 * Checks whether the current environment is actually a desktop or laptop,
 * even when the mobile view is being inspected in browser DevTools.
 */
export function isDesktopOrLaptop() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return true;

  const ua = navigator.userAgent || '';
  const isMobileUA = /android|iPhone|iPad|iPod/i.test(ua);

  // 1. If user-agent is standard desktop (Mac, Windows, Linux) without mobile spoofing
  if (!isMobileUA) return true;

  // 2. DevTools check: Chrome / Edge on desktop emulating iOS.
  // Apple Safari/WebKit on real iOS NEVER has navigator.userAgentData.
  if (/iPhone|iPad|iPod/i.test(ua) && 'userAgentData' in navigator) {
    return true;
  }

  // 3. Physical screen width check:
  // When inspecting in "Responsive" mode or on laptop screens,
  // the physical monitor screen.width is >= 1024px.
  if (typeof window.screen !== 'undefined' && window.screen.width >= 1024) {
    return true;
  }

  // 4. WebGL GPU renderer check:
  // Detects desktop graphics hardware (Intel, NVIDIA, AMD, Apple M-series, ANGLE desktop wrappers)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '').toLowerCase();
        if (
          /nvidia|geforce|radeon|intel|direct3d|swiftshader|angle \(apple|apple m\d|angle \(intel|angle \(nvidia|angle \(amd/i.test(
            renderer
          )
        ) {
          return true;
        }
      }
    }
  } catch (_) {
    // ignore
  }

  return false;
}

export function getMobilePlatform() {
  if (typeof navigator === 'undefined') return null;

  // If viewing on a laptop/desktop (including inspecting mobile view in DevTools), return null
  if (isDesktopOrLaptop()) return null;

  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
  ) {
    return 'ios';
  }
  return null;
}

function buildAndroidIntentUrl(path, fallbackUrl) {
  // If the app isn't installed, Chrome directly navigates to the destination web URL
  const fallback = fallbackUrl || window.location.href;
  return (
    `intent://${path}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(fallback)};end`
  );
}

/**
 * Tries to open `path` in the app.
 *
 * - iOS:     Navigates to the Universal Link (https://web.jivebird.com/...).
 *            If the app is installed and has claimed the domain, iOS opens the app.
 *            If not, Safari loads the web page normally — no error dialog.
 * - Android: Uses Chrome intent with fallback URL pointing to the web page.
 * - Desktop: Returns false so the caller can do normal web navigation.
 *
 * Returns false on desktop so the caller can keep its normal web behaviour.
 */
export function openInApp(path, { fallbackUrl } = {}) {
  const platform = getMobilePlatform();
  if (!platform) return false;

  const targetHttpUrl = fallbackUrl || `https://web.jivebird.com/${path}`;

  if (platform === 'android') {
    window.location.href = buildAndroidIntentUrl(path, targetHttpUrl);
  } else {
    // On iOS: Universal Link navigation prevents the "address is invalid" Safari error.
    // If the app is installed, iOS opens the app. If not, it loads the web page smoothly.
    window.location.href = targetHttpUrl;
  }
  return true;
}
